import styled, { keyframes } from "styled-components";

import {
  Button,
  ButtonIcon,
  Caption,
  Flex,
  Input,
  Title,
} from "@olist/design-system";

type DSTheme = "light" | "dark";

export const ChatContainer = styled(Flex) <{
  DSTheme?: DSTheme;
  isPortal?: boolean;
}>`
  background: ${({ DSTheme }) =>
    DSTheme === "dark"
      ? "linear-gradient(169.32deg, #242424 0%, #4B5C84 100.45%)"
      : "linear-gradient(180deg, #FFFFFF 0%, #F6F6F9 100%)"};
  border-bottom-left-radius: ${({ isPortal }) => isPortal && "40px"};
  border-top-left-radius: ${({ isPortal }) => isPortal && "40px"};
  box-shadow: 0px 8px 16px 0px rgba(11, 19, 74, 0.1);
  box-shadow: 0px 16px 32px 0px rgba(11, 19, 74, 0.08);
  box-shadow: 0px 32px 64px 0px rgba(11, 19, 74, 0.06);
  flex-direction: column;

  justify-content: space-between;
  min-height: 100vh;

  min-height: -webkit-fill-available;
  padding: 32px 24px;
  row-gap: 16px;

  @media (max-width: 768px) {
    overflow-y: scroll;
    padding-bottom: 50px;
  }
  @media (max-width: 376px) {
    overflow-y: scroll;
    padding-bottom: 50px;
    margin-bottom: 50px;
  }
`;

export const Message = styled(Caption) <{ sender: "agent" | "user" }>`
  background-color: ${({ theme }) => theme.olist.color.neutral.softest};
  border-radius: ${({ theme }) => theme.olist.borderRadius.large};
  padding: ${({ theme }) => theme.olist.spacing.inset.small};
`;

export const Footer = styled(Flex)`
  align-items: center;
  column-gap: ${({ theme }) => theme.olist.spacing.inline.medium};
  justify-content: space-between;
  width: 100%;

  button {
    height: 48px;
    margin-top: 8px;
  }
`;

export const WelcomeName = styled(Title) <{ DSTheme?: "dark" | "light" }>`
  -webkit-text-fill-color: transparent;
  background: ${({ DSTheme }) =>
    DSTheme === "dark"
      ? "linear-gradient(126.22deg, #427AFF 64.74%, #5FC2FF 79.48%)"
      : "linear-gradient(146deg, #0015ff 32.08%, #009eff 64.58%)"};
  background-clip: text;
  font-size: 36px;
  font-style: normal;
  font-weight: 400;
  text-align: start;
  width: 100%;
  @media (max-width: 768px) {
    font-size: 26px;
  }
`;

export const WelcomeTitle = styled(Title)`
  font-size: 24px;
  font-style: normal;
  font-weight: 400;
  text-align: start;
  width: 100%;
  @media (max-width: 768px) {
    font-size: 26px;
  }
`;

export const OptionButton = styled(Button) <{ DSTheme?: DSTheme }>`
  align-items: flex-start;
  background: ${({ DSTheme }) =>
    DSTheme === "dark" ? "rgba(36, 36, 36, 1)" : "rgba(242, 240, 233, 1)"};
  border: 2px solid transparent;
  border-radius: 16px;
  box-shadow: 0 2px 4px 0 rgba(11, 19, 74, 0.08),
    0 4px 8px 0 rgba(11, 19, 74, 0.06), 0 8px 16px 0 rgba(11, 19, 74, 0.04);

  box-sizing: border-box;
  color: ${({ DSTheme }) => DSTheme === "dark" && "rgba(237, 239, 241, 1)"};
  display: flex;

  flex-direction: column;
  padding: 16px;
  position: relative;
  row-gap: 10px;

  &:hover {
    background: ${({ DSTheme }) =>
    DSTheme === "dark"
      ? "linear-gradient(rgba(36, 36, 36, 1), rgba(36, 36, 36, 1))"
      : "linear-gradient(rgba(242, 240, 233, 1), rgba(242, 240, 233, 1))"}
        padding-box,
      /* Inner background */
        conic-gradient(
          from 90deg at 50% 50%,
          #8297f3 61.200000643730164deg,
          #b9cff5 86.39999806880951deg,
          #8d8dff 125.99999785423279deg,
          #6e9dee 208.7999939918518deg,
          #6e9dee 208.7999939918518deg,
          #71c9ff 291.6000008583069deg,
          #86fff5 331.2000060081482deg
        )
        border-box;
    background-clip: padding-box, border-box;
    background-origin: border-box;
  }

  width: 100%;

  svg {
    color: ${({ DSTheme }) => DSTheme === "dark" && "rgba(237, 239, 241, 1)"};
    width: 18px;
  }
`;

export const GradientText = styled(Caption) <{ DSTheme?: DSTheme }>`
  -webkit-text-fill-color: transparent;
  background: ${({ DSTheme }) =>
    DSTheme === "dark"
      ? "linear-gradient(126.22deg, #427AFF 64.74%, #5FC2FF 79.48%)"
      : "linear-gradient(146deg, #0015ff 32.08%, #009eff 64.58%)"};
  background-clip: text;
  font-size: 16px;
  font-weight: 500;
`;

const textShine = keyframes`
  0% {
      background-position: 0% 50%;
  }
  100% {
      background-position: 100% 50%;
  }
`;

export const GradientTextAnimated = styled(Caption) <{ DSTheme?: DSTheme }>`
  -webkit-text-fill-color: transparent;
  animation: ${textShine} 1s ease-in-out infinite alternate;
  background: ${({ DSTheme }) =>
    DSTheme === "dark"
      ? "linear-gradient(126.22deg, #427AFF 64.74%, #5FC2FF 79.48%)"
      : "linear-gradient(146deg, #0015ff 32.08%, #009eff 64.58%)"};
  background-clip: text;
  background-size: 500% auto;
  font-size: 18px;
  font-weight: 500;
`;

export const Text = styled(Caption) <{ DSTheme?: DSTheme }>`
  font-size: 18px;
  font-weight: 500;
`;

const typing = keyframes`
  from { 
    max-width: 0;
    height: 0;
  }
  to {
    max-width: 500px;
    height: fit-content;
  }
`;

const blinkTextCursor = keyframes`
  50% { border-color: transparent; } 
`;

export const MarkdownContainer = styled(Flex)`
  background-color: ${({ theme }) => theme.olist.color.neutral.softest};
  border-radius: ${({ theme }) => theme.olist.borderRadius.large};
  flex-direction: column;
  height: fit-content;
  padding: ${({ theme }) => theme.olist.spacing.inset.small};
  position: relative;
  row-gap: 8px;

  .italic {
    font-style: italic;
    text-align: right;
  }

  .typing-content {
    animation: ${typing} steps(var(--chars)) var(--dur) var(--delay) forwards,
      ${blinkTextCursor} 0.8s step-end infinite var(--delay);
    border-right: 2px solid currentColor;
    display: inline-block;
    margin: 4px 0;
    max-width: 0;
    overflow: hidden;
    white-space: pre;
  }

  .typing-content:first-child {
    margin-bottom: 4px;
    margin-top: 0px;
  }

  .typing-content.done {
    white-space: normal; /* libera quebra depois da animação */
    word-wrap: break-word;
  }
  .typing-content.typing-last {
    border-right: 2px solid currentColor;
  }
  .typing-content:not(.typing-last) {
    border-right: none;
  }
`;

export const SInput = styled(Input) <{ DSTheme?: DSTheme }>`
  background-color: ${({ DSTheme }) => DSTheme === "dark" && "black"};
  border-radius: 8px;
  color: ${({ DSTheme }) => DSTheme === "dark" && "white"};
`;

export const SButtonIcon = styled(ButtonIcon) <{ DSTheme?: DSTheme }>`
  background-color: ${({ DSTheme }) => DSTheme === "dark" && "transparent"};
  color: ${({ DSTheme }) => DSTheme === "dark" && "white"};
`;

export const STable = styled.table`
  border-collapse: separate;
  border-radius: 8px;
  border-spacing: 0;
  overflow-x: auto;
  width: 100%;

  td,
  th {
    border: 1px solid #ddd;
    padding: 8px;
  }

  tr:first-child th:first-child {
    border-top-left-radius: 0.5rem;
  }

  tr:first-child th:last-child {
    border-top-right-radius: 0.5rem;
  }

  tr:last-child td:first-child {
    border-bottom-left-radius: 0.5rem;
  }

  tr:last-child td:last-child {
    border-bottom-right-radius: 0.5rem;
  }

  tr:nth-child(odd) {
    background-color: rgba(243, 243, 252, 1);
  }
  tr:nth-child(even) {
    background-color: rgba(255, 255, 255, 1);
  }

  tr:hover {
    background-color: #ddd;
  }

  th {
    background-color: rgba(234, 236, 238, 1);
    color: rgba(80, 85, 124, 1);
    font-weight: 700;
    padding-bottom: 12px;
    padding-top: 12px;
    text-align: left;
  }
`;

export const HistoryContainer = styled(Flex)`
  flex-direction: column;
  gap: ${({ theme }) => theme.olist.spacing.stack.medium};
  justify-content: flex-start;
  max-height: 64vh;
  min-height: 0;
  overflow-y: scroll;

  @media (max-width: 768px) {
    min-height: 0;
    max-height: 64vh;
  }
`;

export const TransferDivider = styled.div`
  color: ${({ theme }) => theme.olist.color.neutral.base};
  display: inline-block;
  font-size: 13px;
  position: relative;
  text-align: center;

  &::before,
  &::after {
    border-top: 1px solid #ccc;
    content: "";
    position: absolute;
    top: 50%;
    width: 50px;
  }

  &::before {
    left: 0;
  }

  &::after {
    right: 0;
  }
`;
